import React, {ReactNode, useRef, useState} from 'react';
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import {sx} from './sx'
import {classNames} from "@helpers/classNames";
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import TaskActionsModalSingleVoyage
    from "@containers/singleVoyage/tasks/taskActions/TaskActionsModalSingleVoyage";
import {useTranslation} from 'react-i18next';
import {ITaskSingleVoyage} from "@containers/singleVoyage/tasks/types";
import SubTasksTaskInfoViewSingleVoyage
    from "@containers/singleVoyage/tasks/views/pointTasksInfo/itemTask/SubTasksTaskInfoViewSingleVoyage";
import TasksFilesListModalSingleVoyage
    from "@containers/singleVoyage/tasks/tasksFiles/tasksFilesList/TasksFilesListModalSingleVoyage";
import {
    collectFilesTaskSingleVoyage,
    countOpenExecutionsTaskSingleVoyage,
    taskStateSingleVoyage
} from "@containers/singleVoyage/tasks/helpers/executions";
import TaskStateDotSingleVoyage
    from "@containers/singleVoyage/tasks/views/pointTasksInfo/itemTask/TaskStateDotSingleVoyage";
import ChangeTaskStatusSingleVoyage
    from "@containers/singleVoyage/tasks/changeTaskStatus/ChangeTaskStatusSingleVoyage";


interface IProps {
    task: ITaskSingleVoyage,
    showDescription?: boolean,
    childrenHeader?: ReactNode
    hasChangeStatus?: boolean,
    voyageId?: number,
    accountId: string | number
    /** The stop the task is shown in the context of. */
    currentWaypointId?: number
    /** Scope the task to that one stop, instead of showing all of its stops. */
    narrowToWaypoint?: boolean
    /**
     * true  - the side panel: a block with the description, the subtasks and the
     *         dispatcher's controls.
     * false - the stop cards on the left: one row, read only.
     */
    detailed?: boolean
    /** Position of the task in the list. Subtasks get 1.1, 1.2 and so on. */
    number?: number
}

/**
 * One task, in one of two registers.
 *
 * On the left it is a line in a list: what the work is and how it stands here,
 * one row per task including its subtasks, nothing to press. On the right it is
 * the same task opened up - description, subtasks spelled out, and the controls
 * to close it. Reading and doing used to be mixed into both columns, which is
 * why neither of them fit its width.
 */
const ItemTaskInfo = ({
                          task,
                          showDescription,
                          childrenHeader,
                          hasChangeStatus,
                          voyageId,
                          accountId,
                          currentWaypointId,
                          narrowToWaypoint,
                          detailed,
                          number,
                      }: IProps
) => {
    const {t} = useTranslation()

    // photoRequired arrives as the string 'YES'/'NO' - both are truthy in JS,
    // so compare explicitly, otherwise the camera icon is always lit.
    const photoRequired = task.config?.photoRequired === 'YES'
    const inPointContext = typeof currentWaypointId === 'number'
    const narrow = !!narrowToWaypoint && inPointContext

    /**
     * Scoped to a stop, a subtask is listed only where it is actually done. The
     * same rule the tasks themselves are listed by: what this stop shows is the
     * executions that exist at it. A task that looks childless here and parented
     * at the next stop is not a contradiction - it is the answer to "what is
     * there to do here".
     */
    const visibleSubtasks = narrow
        ? task.subtasks?.filter((subtask) => subtask.executions
            ?.some((execution) => execution.waypointId === Number(currentWaypointId))) ?? []
        : task.subtasks ?? []

    // In the context of a stop we go by its execution, not by the task roll-up.
    const contextExecution = inPointContext
        ? task.executions?.find((execution) => execution.waypointId === Number(currentWaypointId))
        : undefined

    // Scoped to a stop, the photos are that stop's. Showing the whole task, one
    // entry gathers them all - the strip below only marks which stops have any.
    const files = narrow
        ? contextExecution?.files ?? []
        : [task, ...(task.subtasks ?? [])].flatMap(collectFilesTaskSingleVoyage)
    const {open} = countOpenExecutionsTaskSingleVoyage(task)

    /**
     * Acting on a task happens in the side panel only. The stop cards on the left
     * are a list you read while following the route, and a control on every row
     * there turned that list into a toolbar - in a column that could not spare the
     * width for one. A right click anywhere on the block opens the whole-task
     * close, the one action that asks a question before it runs.
     */
    const actionsRef = useRef<HTMLDivElement>(null)
    const [acting, setActing] = useState(false)
    const canCloseTask = !!hasChangeStatus && !!detailed && open >= 1
    // Only an open stop can still be closed; a finished one offers nothing.
    const stopAction = hasChangeStatus && detailed && narrow && contextExecution?.status === 'PLANNED'
        ? contextExecution
        : undefined
    const hasActions = !!detailed && (!!childrenHeader || canCloseTask)

    /**
     * One colour, one meaning, both columns: the dot is the state of the work you
     * are looking at, and in the side panel the block's left edge repeats it at
     * block scale. Scoped to a stop that state is this stop's; otherwise it is the
     * task as a whole.
     */
    const state = taskStateSingleVoyage(narrow ? contextExecution?.status : task.status)

    return (
        <Stack
            gap={detailed ? '3px' : 0}
            sx={detailed ? sx.taskCard : sx.taskRow}
            className={classNames(state, acting && 'acting')}
            onContextMenu={hasActions ? (event) => {
                event.preventDefault()
                actionsRef.current?.querySelector('button')?.click()
            } : undefined}
        >
            <Stack
                gap={'6px'}
                direction={'row'}
                justifyContent={'space-between'}
                alignItems={'center'}
                sx={sx.fullWidth}
            >
                <Stack
                    gap={'4px'}
                    direction={'row'}
                    alignItems={'center'}
                    sx={{minWidth: 0}}
                >
                    <TaskStateDotSingleVoyage state={state}/>

                    {photoRequired && <PhotoCameraIcon
                        sx={sx.cameraIcon}
                        titleAccess={t('singleVoyage.photoReport')}
                    />}

                    {typeof number === 'number' && <Typography sx={sx.taskNumber}>
                        {number}.
                    </Typography>}

                    <Typography sx={sx.name} noWrap>
                        {task.title}
                    </Typography>
                </Stack>

                <Stack
                    gap={'3px'}
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={'flex-end'}
                    sx={{flexShrink: 0}}
                >
                    {/* Photos stay in the open: they are evidence, not an action. */}
                    {files.length >= 1 && <TasksFilesListModalSingleVoyage
                        btnVariant={'icon'}
                        files={files}
                    />}

                    {stopAction && <ChangeTaskStatusSingleVoyage
                        taskId={task.id}
                        voyageId={Number(voyageId)}
                        execution={stopAction}
                        accountId={accountId}
                    />}

                    {hasActions && <Box ref={actionsRef} sx={{display: 'flex'}}>
                        <TaskActionsModalSingleVoyage
                            task={task}
                            accountId={accountId}
                            voyageId={voyageId}
                            canCloseTask={canCloseTask}
                            extra={childrenHeader}
                            onOpenChange={setActing}
                        />
                    </Box>}
                </Stack>
            </Stack>

            {detailed && showDescription && task.description && <Typography sx={sx.description}>
                {task.description}
            </Typography>}

            {visibleSubtasks.length >= 1 && <SubTasksTaskInfoViewSingleVoyage
                subtasks={visibleSubtasks}
                readOnly={!detailed}
                hasChangeStatus={hasChangeStatus}
                voyageId={voyageId}
                accountId={accountId}
                currentWaypointId={currentWaypointId}
                narrowToWaypoint={narrowToWaypoint}
                parentNumber={number}
            />}

        </Stack>
    );
};

export default ItemTaskInfo;
